import * as React from 'react';
import { type JSX, type MouseEvent, Suspense, useEffect, useRef, useState } from 'react';
import { setShowAuth, useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button.tsx';
import { ChevronDownIcon, SearchIcon, UserIcon } from 'lucide-react';
import { useUpdateUserMutation } from '@/app/auth/api/queries';
import { Separator } from '@/components/ui/separator';
import { LazyLoader } from '@/components/app/lazy-loader.tsx';
import { setShowTopSearch } from '@/stores/ui.store.ts';
import { type MenuItem, menuItems, useHeaderNavigation } from '@/app/layout/use-header-navigation.tsx';
import { cn } from '@/lib/utils.ts';
import { MobileDrawer } from '@/app/layout/mobile-drawer.tsx';
import { Link, useLocation } from '@tanstack/react-router';
import { useRipple } from '@/hooks/use-ripple.ts';

const AuthDialog = React.lazy(() =>
  import('@/app/auth/auth-dialog.tsx').then((module) => ({
    default: module.AuthDialog,
  })),
);

export function Header() {
  const PrefetchAuth = useRef<() => JSX.Element | null>(null);
  const firstRender = useRef(true);
  const updateUserMutation = useUpdateUserMutation();
  const showAuth = useAuthStore((state) => state.showAuth);
  const [isScrolled, setIsScrolled] = useState(false);
  const { ref } = useRipple();

  const location = useLocation();

  const { onMenuClick, user, userMenuItems, hoverOn, hoverOff, onSubClick } = useHeaderNavigation();

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      // if (user && user.id) {
        updateUserMutation.mutate();
      // }
      const loadAuth = async () => {
        const module = await import('@/app/auth/auth-dialog.tsx');
        PrefetchAuth.current = module.AuthDialog;
      };
      setTimeout(loadAuth, 1);
    }

    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsScrolled(true);
      } else {
        if (window.scrollY < 100) {
          setIsScrolled(false);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith('login') && showAuth) {
      setShowAuth(false);
    }
  }, [location.pathname]);

  return (
    <header id="header-navigation" className=" sticky top-0 bg-white z-10 border-b border-b-gray-300">
      <div
        ref={ref}
        className={cn(
          'grid grid-cols-3 md:flex items-center px-4 h-16 justify-between max-w-8xl mx-auto transition-all duration-300',
          isScrolled ? 'md:h-16' : 'md:h-28',
        )}
      >
        <MobileDrawer />
        <Link to="/" viewTransition={true} aria-label="Sacco Carpet Logo" className="flex items-center justify-center">
          <img
            className={cn('h-[50px] md:h-20 transition-all duration-300', isScrolled ? 'md:h-[50px]' : 'md:h-20')}
            src="/media/logo-black-full.svg"
            alt="Sacco Carpet"
          />
        </Link>
        <div className="flex items-center justify-end">
          <div className="nav flex items-center">
            <div className="hidden md:flex space-x-4">
              {menuItems.map((item, i) =>
                item.sub ? (
                  <div key={item.href + i} className="relative" onMouseLeave={hoverOff}>
                    <Button
                      variant="ghost"
                      className="inline-flex items-center cursor-pointer ripple"
                      onMouseEnter={hoverOn}
                      onClick={onSubClick}
                    >
                      {item.text}
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                    </Button>
                    <SubMenu items={item.sub} onClick={onMenuClick} />
                  </div>
                ) : (
                  <Button key={item.href + i} asChild variant="ghost" className="ripple">
                    <a href={item.href} onClick={(e) => onMenuClick(e, item)}>
                      {item.text}
                    </a>
                  </Button>
                ),
              )}
            </div>
            <Button variant="ghost" className="mx-1 lg:ml-8 xl:ml-24" onClick={() => setShowTopSearch(true)}>
              <SearchIcon />
            </Button>
            {user ? (
              <div className="relative" onMouseLeave={hoverOff}>
                <Button variant="ghost" className="uppercase" onMouseEnter={hoverOn} onClick={onSubClick}>
                  <UserIcon />
                  <span className="hidden md:inline">{user.name}</span>
                </Button>
                <SubMenu items={userMenuItems} onClick={onMenuClick} last={true} />
              </div>
            ) : (
              <Button variant="ghost" className="uppercase" onClick={() => setShowAuth(true)}>
                Login
              </Button>
            )}
            {showAuth && (
              <Suspense fallback={<LazyLoader />}>
                <AuthDialog />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

type SubMenuProps = {
  items: MenuItem[];
  onClick: (e: MouseEvent<HTMLAnchorElement>, item: MenuItem) => void;
  last?: boolean;
};

const SubMenu = ({ items, onClick, last = false }: SubMenuProps) => {
  return (
    <div
      className={cn(
        'sub absolute hidden transform shadow-md opacity-0 transition-all duration-400 transition-discrete bg-white',
        last ? 'right-0 left-auto xl:left-1/2 xl:-translate-x-1/2 xl:right-auto' : 'left-1/2 -translate-x-1/2',
      )}
    >
      {items.map((item, i) =>
        item.text === '---' ? (
          <Separator key={item.href} />
        ) : (
          <a
            key={item.href + i}
            href={item.href}
            className="ripple ripple-no-navigate pl-4 pr-10 py-1.5 text-sm hover:bg-gray-100 uppercase text-left flex gap-1 items-center transition-all duration-300"
            onClick={(e) => onClick(e, item)}
          >
            {item.icon ? item.icon : <span className="w-4"></span>}
            {item.text}
          </a>
        ),
      )}
    </div>
  );
};
